import { useState, type KeyboardEvent } from "react";
import type { Authorization, Channel, Product, Promotions } from "../../api/types";
import { useTill } from "../../app/till-context";
import { t } from "../../i18n";
import { formatAmount, parseAmount } from "../../lib/money";
import { dayDescription, dayPromotionName, isDrink } from "../promotions/promotions";
import { productsIn, sellableCategories } from "../sale/menu-view";
import { DayMailButton } from "./DayMailButton";
import { DayPromotionDialog } from "./DayPromotionDialog";
import { MailingPanel } from "./MailingPanel";

interface Props {
  /** The owner's Beheer code: only the owner changes the actions (prototype). */
  authorization: Authorization;
}

type ActionId = keyof Promotions;
type DayId = "day" | "day2";

const ACTIONS: ActionId[] = ["day", "day2", "auto", "free", "points"];
const CHANNELS: Channel[] = ["takeaway", "delivery", "table"];

/** "30" or "7,50", as the owner types amounts. */
const euros = (cents: number) => (cents % 100 === 0 ? String(cents / 100) : formatAmount(cents));

/** A number field that saves when it is left or with Enter, as the prototype's fields did. */
function NumberField({
  value,
  label,
  disabled,
  onSave,
}: {
  value: string;
  label: string;
  disabled: boolean;
  onSave: (text: string) => void;
}) {
  const [text, setText] = useState<string | null>(null);
  const done = () => {
    if (text !== null && text !== value) onSave(text);
    setText(null);
  };
  return (
    <input
      inputMode="decimal"
      value={text ?? value}
      aria-label={label}
      disabled={disabled}
      onChange={(event) => {
        setText(event.target.value);
      }}
      onBlur={done}
      onKeyDown={(event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Enter") event.currentTarget.blur();
      }}
    />
  );
}

/**
 * Beheer › Acties, as in the prototype: four actions in a table, per action the channels it holds for
 * (Afhaal, Levering, Tafel) and Aan/Uit. Every change is saved at once and is active on every device;
 * the server applies the actions when it prices an order.
 */
export function PromotionsScreen({ authorization }: Props) {
  const { api, menu, promotions, promotionsChanged, report, toast } = useTill();
  const [busy, setBusy] = useState(false);
  const [editingDay, setEditingDay] = useState<DayId | null>(null);
  const data = menu.menu;
  if (data === null || promotions === null) return null;
  const text = t.promotions;

  const save = async (next: Promotions): Promise<boolean> => {
    if (busy) return false;
    setBusy(true);
    try {
      const answer = await api.request<{ promotions: Promotions }>("PUT", "/device/promotions", {
        staffToken: authorization.token,
        body: next,
      });
      promotionsChanged(answer.promotions);
      toast(text.saved);
      return true;
    } catch (error) {
      report(error);
      return false;
    } finally {
      setBusy(false);
    }
  };
  const change = <K extends ActionId>(id: K, update: Partial<Promotions[K]>) =>
    void save({ ...promotions, [id]: { ...promotions[id], ...update } });

  // the free product: drinks first, then the other products, in menu order (prototype)
  const products = sellableCategories(data).flatMap((category) => productsIn(data, category.id));
  const drinks = products.filter((product) => isDrink(data, product));
  const others = products.filter((product) => !isDrink(data, product));
  const chooseFree = (product: Product | undefined) => {
    if (!product) return;
    const keep = product.variants.some((variant) => variant.label === promotions.free.variant);
    const variant = keep ? promotions.free.variant : (product.variants.at(-1)?.label ?? null);
    change("free", { productId: product.id, variant: product.variants.length > 1 ? variant : null });
  };

  const { auto, free, points } = promotions;
  const detail = (id: ActionId) => {
    switch (id) {
      case "day":
      case "day2":
        return (
          <span className="inl">
            <span className="note">
              {promotions[id].categoryId === null ? text.day.notSet : dayDescription(promotions[id], data)}
            </span>
            <button
              type="button"
              className="btn small"
              disabled={busy}
              onClick={() => {
                setEditingDay(id);
              }}
            >
              {text.day.edit}
            </button>
          </span>
        );
      case "auto":
        return (
          <span className="inl">
            <NumberField
              value={String(auto.percent)}
              label={text.percent}
              disabled={busy}
              onSave={(value) => {
                const percent = Number.parseInt(value, 10);
                if (Number.isFinite(percent)) change("auto", { percent: Math.max(1, Math.min(50, percent)) });
              }}
            />
            {text.percentFrom}
            <NumberField
              value={euros(auto.minimumCents)}
              label={text.minimum}
              disabled={busy}
              onSave={(value) => {
                const minimumCents = parseAmount(value);
                if (minimumCents !== null) change("auto", { minimumCents });
              }}
            />
          </span>
        );
      case "free":
        return (
          <span className="inl">
            <select
              aria-label={text.product}
              value={free.productId ?? ""}
              disabled={busy}
              onChange={(event) => {
                chooseFree(data.products[event.target.value]);
              }}
            >
              {free.productId === null && <option value="" />}
              {(
                [
                  [text.drinks, drinks],
                  [text.others, others],
                ] as const
              ).map(([label, list]) => (
                <optgroup key={label} label={label}>
                  {list.map((product) => (
                    <option key={product.id} value={product.id}>
                      {product.name}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
            {text.freeFrom}
            <NumberField
              value={euros(free.minimumCents)}
              label={text.minimum}
              disabled={busy}
              onSave={(value) => {
                const minimumCents = parseAmount(value);
                if (minimumCents !== null) change("free", { minimumCents });
              }}
            />
          </span>
        );
      case "points":
        // per shop its own choice (Furkan, 01/10/2026): from which amount, which point, how much off
        return (
          <span className="inl">
            {text.pointsEdit.from}
            <NumberField
              value={euros(points.fromCents)}
              label={text.pointsEdit.fromLabel}
              disabled={busy}
              onSave={(value) => {
                const fromCents = parseAmount(value);
                if (fromCents !== null) change("points", { fromCents });
              }}
            />
            {text.pointsEdit.middle}
            <NumberField
              value={String(points.goal)}
              label={text.pointsEdit.goalLabel}
              disabled={busy}
              onSave={(value) => {
                const goal = Number.parseInt(value, 10);
                if (Number.isFinite(goal)) change("points", { goal: Math.max(1, Math.min(100, goal)) });
              }}
            />
            {text.pointsEdit.reward}
            <NumberField
              value={euros(points.rewardCents)}
              label={text.pointsEdit.rewardLabel}
              disabled={busy}
              onSave={(value) => {
                const rewardCents = parseAmount(value);
                if (rewardCents !== null && rewardCents > 0) change("points", { rewardCents });
              }}
            />
            {text.pointsEdit.off}
          </span>
        );
    }
  };

  return (
    <>
      <div className="panel admin-section">
        <h2>{text.title}</h2>
        <p className="note">{text.note}</p>
        <div className="tablewrap">
          <table className="promotbl">
            <thead>
              <tr>
                <th>{text.columns.action}</th>
                {CHANNELS.map((channel) => (
                  <th key={channel} className="c">
                    {text.channels[channel]}
                  </th>
                ))}
                <th className="c">{text.columns.on}</th>
              </tr>
            </thead>
            <tbody>
              {ACTIONS.map((id) => {
                const action = promotions[id];
                const name =
                  id === "day" || id === "day2"
                    ? dayPromotionName(data, promotions[id], text.names[id])
                    : text.names[id];
                return (
                  <tr key={id} className={action.on ? "" : "off"}>
                    <td className="pmain">
                      <b>{name}</b>
                      <br />
                      <span className="pdetail">{detail(id)}</span>
                      {id === "day" && <DayMailButton authorization={authorization} day={promotions.day} />}
                    </td>
                    {CHANNELS.map((channel) => (
                      <td key={channel} className="c">
                        {/* on a phone the word stands above its button: the row has no head there */}
                        <span className="plabel" aria-hidden="true">
                          {text.channels[channel]}
                        </span>
                        <button
                          type="button"
                          className="btn tog"
                          aria-pressed={action.channels[channel]}
                          aria-label={text.at(name, text.channels[channel])}
                          disabled={busy}
                          onClick={() => {
                            change(id, { channels: { ...action.channels, [channel]: !action.channels[channel] } });
                          }}
                        >
                          {action.channels[channel] ? "✓" : "–"}
                        </button>
                      </td>
                    ))}
                    <td className="c">
                      <span className="plabel" aria-hidden="true">
                        {text.columns.on}
                      </span>
                      <button
                        type="button"
                        className="btn tog on"
                        aria-pressed={action.on}
                        aria-label={`${name}: ${action.on ? text.on : text.off}`}
                        disabled={busy}
                        onClick={() => {
                          change(id, { on: !action.on });
                        }}
                      >
                        {action.on ? text.on : text.off}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {editingDay !== null && (
          <DayPromotionDialog
            menu={data}
            day={promotions[editingDay]}
            busy={busy}
            onSave={(day) => {
              void save({ ...promotions, [editingDay]: day }).then((saved) => {
                if (saved) setEditingDay(null);
              });
            }}
            onClose={() => {
              setEditingDay(null);
            }}
          />
        )}
      </div>
      <MailingPanel authorization={authorization} />
    </>
  );
}
